import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../platform/AuthContext";
import { api } from "../lib/api";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-white/40 transition-colors focus:border-electric focus:outline-none";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // login | forgot | forgotSent
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api("/auth/forgot-password", { method: "POST", body: { email } });
        setMode("forgotSent");
      } else {
        const user = await login(email, password);
        navigate(user.role === "admin" ? "/admin" : "/espace", { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-nuit-950 px-5 py-12">
      <div className="w-full max-w-sm">
        <Link to="/">
          <img src="/assets/logo-white.png" alt="Noveocard" className="mx-auto h-7 w-auto" width="169" height="24" />
        </Link>
        <div className="mt-8 rounded-3xl border border-white/10 bg-nuit-900/60 p-7 backdrop-blur" data-testid="login-card">
          {mode === "forgotSent" ? (
            <div className="text-center" data-testid="forgot-sent">
              <h1 className="font-display text-xl font-bold text-white">Vérifiez vos e-mails</h1>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                Si un compte existe avec cette adresse, un lien de réinitialisation vient d'être
                envoyé (valable 1 heure).
              </p>
              <button onClick={() => setMode("login")} className="mt-6 font-display text-sm font-semibold text-electric-300">
                ← Retour à la connexion
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-xl font-bold text-white">
                {mode === "login" ? "Connexion à votre espace" : "Mot de passe oublié"}
              </h1>
              <p className="mt-1.5 text-sm text-white/55">
                {mode === "login"
                  ? "Administration Noveocard et espaces commerçants."
                  : "Indiquez votre e-mail : nous vous envoyons un lien sécurisé."}
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="lg-email" className="mb-1.5 block text-sm font-semibold text-white/80">E-mail</label>
                  <input id="lg-email" type="email" required className={inputCls} value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.fr" data-testid="login-email" />
                </div>
                {mode === "login" && (
                  <div>
                    <label htmlFor="lg-password" className="mb-1.5 block text-sm font-semibold text-white/80">Mot de passe</label>
                    <input id="lg-password" type="password" required className={inputCls} value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password" />
                  </div>
                )}
                {error && (
                  <p className="rounded-xl bg-red-500/10 p-3 text-sm font-medium text-red-300" role="alert" data-testid="login-error">
                    {error}
                  </p>
                )}
                <button type="submit" disabled={loading} data-testid="login-submit"
                  className="w-full rounded-full bg-electric py-3.5 font-display text-sm font-semibold text-white transition-colors hover:bg-electric-600 disabled:opacity-50">
                  {loading ? "Patience…" : mode === "login" ? "Se connecter" : "Envoyer le lien"}
                </button>
              </form>
              <button
                onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setError(""); }}
                className="mt-5 w-full text-center text-sm font-medium text-white/55 transition-colors hover:text-white"
                data-testid="login-toggle-forgot"
              >
                {mode === "login" ? "Mot de passe oublié ?" : "← Retour à la connexion"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("8 caractères minimum.");
    if (password !== confirm) return setError("Les deux mots de passe ne correspondent pas.");
    setLoading(true);
    try {
      await api("/auth/reset-password", { method: "POST", body: { token, password } });
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-nuit-950 px-5 py-12">
      <div className="w-full max-w-sm">
        <img src="/assets/logo-white.png" alt="Noveocard" className="mx-auto h-7 w-auto" width="169" height="24" />
        <div className="mt-8 rounded-3xl border border-white/10 bg-nuit-900/60 p-7 backdrop-blur" data-testid="reset-card">
          {done ? (
            <div className="text-center">
              <h1 className="font-display text-xl font-bold text-white">Mot de passe modifié</h1>
              <p className="mt-3 text-sm text-white/60">Vous pouvez maintenant vous connecter.</p>
              <button onClick={() => navigate("/connexion")} data-testid="reset-goto-login"
                className="mt-6 w-full rounded-full bg-electric py-3.5 font-display text-sm font-semibold text-white">
                Se connecter
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-xl font-bold text-white">Nouveau mot de passe</h1>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <input type="password" required className={inputCls} value={password} minLength={8}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Nouveau mot de passe (8 caractères min.)"
                  data-testid="reset-password" aria-label="Nouveau mot de passe" />
                <input type="password" required className={inputCls} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} placeholder="Confirmation"
                  data-testid="reset-confirm" aria-label="Confirmation du mot de passe" />
                {error && (
                  <p className="rounded-xl bg-red-500/10 p-3 text-sm font-medium text-red-300" role="alert">{error}</p>
                )}
                <button type="submit" disabled={loading} data-testid="reset-submit"
                  className="w-full rounded-full bg-electric py-3.5 font-display text-sm font-semibold text-white disabled:opacity-50">
                  {loading ? "Patience…" : "Enregistrer"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
